import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Check, ArrowDownToLine, ArrowUpFromLine, Loader2, CheckCircle2, Circle } from "lucide-react";
import { Header } from "@/components/Header";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { copyText } from "@/lib/mobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

function StepRow({ done, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {done ? (
        <CheckCircle2 size={18} className="text-[#00E676]" />
      ) : (
        <Circle size={18} className="text-slate-600" />
      )}
      <span className={done ? "text-slate-200" : "text-slate-500"}>{label}</span>
    </div>
  );
}

export default function Wallet() {
  const { user, setUser } = useAuth();
  const [maxWithdraw, setMaxWithdraw] = useState(20);
  const [copied, setCopied] = useState(false);
  const [network, setNetwork] = useState("ETH");
  const [wdAmount, setWdAmount] = useState("");
  const [recvAddr, setRecvAddr] = useState("");
  const [busy, setBusy] = useState("");
  const [txns, setTxns] = useState([]);
  const [depositNetworks, setDepositNetworks] = useState({});
  const [depositNotifications, setDepositNotifications] = useState({});

  const load = useCallback(async () => {
    const [{ data: w }, { data: t }] = await Promise.all([
      api.get("/wallet"),
      api.get("/transactions"),
    ]);
    setUser(w.user);
    setMaxWithdraw(w.max_withdraw);
    setTxns(t.transactions || []);
    setDepositNetworks(w.deposit_networks || {});
    setDepositNotifications(w.deposit_notifications || {});
  }, [setUser]);

  useEffect(() => {
    let stopped = false;
    let timer;
    const refresh = async () => {
      try {
        await load();
      } catch {
        if (!stopped) setDepositNetworks({});
        if (!stopped) setDepositNotifications({});
      } finally {
        if (!stopped) timer = setTimeout(refresh, 15000);
      }
    };
    refresh();
    return () => { stopped = true; clearTimeout(timer); };
  }, [load]);

  useEffect(() => {
    setRecvAddr(user?.receiving_address || "");
  }, [user?.receiving_address]);

  if (!user || typeof user !== "object") return null;

  const depositAddress = user.deposit_addresses?.[network] || "";
  const networkNames = { TRX: "TRON (TRC20)", ETH: "Ethereum (ERC20)", BSC: "BNB Smart Chain (BEP20)" };
  const depositStatus = depositNetworks[network];
  const manualDeposits = user.deposit_processing_mode === "manual";
  const depositMessage = !user.wallet_receiving_enabled
    ? "Receiving addresses are pending wallet verification. Do not send funds yet."
    : manualDeposits
      ? "Deposits are reviewed manually. After sending USDT, share the network and transaction hash with support. Your balance updates after approval."
    : depositStatus?.ready
      ? "USDT deposits are detected automatically after network confirmation. 1 USDT adds 1 point to your balance."
      : depositStatus?.status === "catching_up"
        ? "Checking earlier blocks for deposits. Your balance will update after confirmation. Please wait before sending new funds."
        : "Deposit monitoring is temporarily unavailable on this network. Existing transfers will be checked when it resumes. Please wait before sending funds.";

  const copy = async () => {
    if (!depositAddress) return;
    try {
      await copyText(depositAddress);
    } catch {
      toast.error("Unable to copy address");
      return;
    }
    setCopied(true);
    toast.success("Deposit address copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const saveAddr = async () => {
    setBusy("addr");
    try {
      const { data } = await api.post("/wallet/receiving-address", { address: recvAddr });
      setUser(data.user);
      toast.success("Receiving address saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const doWithdraw = async () => {
    setBusy("withdraw");
    try {
      const { data } = await api.post("/wallet/withdraw", { amount: parseFloat(wdAmount) });
      setUser(data.user);
      toast.success(data.message);
      setWdAmount("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const profitPct = Math.min(100, (user.earned_profit / 50) * 100); // kept for potential future use

  return (
    <div className="pb-24 grain min-h-screen">
      <Header title="Wallet" />
      <div className="max-w-md mx-auto px-4 pt-5 space-y-4">
        {/* balance card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl border border-[#1E293B] bg-gradient-to-br from-[#121721] to-[#0E2E25]/40 p-5 overflow-hidden"
        >
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-[#00E676]/10 blur-2xl" />
          <p className="text-xs text-slate-400">Total Balance</p>
          <div className="font-mono-num text-4xl font-bold mt-1" data-testid="user-usdt-balance">
            {user.balance.toFixed(2)} <span className="text-lg text-slate-400">USDT</span>
          </div>
          <div className="flex gap-4 mt-4 text-xs">
            <div>
              <div className="text-slate-500">Deposited</div>
              <div className="font-mono-num text-slate-200">{user.total_deposited.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-slate-500">Trading Profit</div>
              <div className={`font-mono-num ${user.earned_profit >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                {user.earned_profit >= 0 ? "+" : ""}
                {user.earned_profit.toFixed(2)}
              </div>
            </div>
          </div>
        </motion.div>

        {/* deposit + withdraw actions */}
        <div className="grid grid-cols-2 gap-3">
          <Dialog>
            <DialogTrigger asChild>
              <button
                data-testid="open-deposit-dialog"
                className="rounded-xl border border-[#1E293B] bg-[#121721] p-4 flex flex-col items-center gap-2"
              >
                <ArrowDownToLine className="text-[#00E676]" size={22} />
                <span className="text-sm font-semibold">Deposit</span>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#121721] border-[#1E293B] text-white max-w-sm">
              <DialogHeader>
                <DialogTitle className="font-display">Deposit USDT</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-xs text-amber-300" role="status">
                  {depositMessage}
                </p>
                {!manualDeposits && Number.isInteger(depositStatus?.last_scanned_block) && (
                  <p className="text-xs text-slate-400">
                    Last scanned block: {depositStatus.last_scanned_block}
                    {Number.isInteger(depositStatus.finalized_block) && ` · Finalized block: ${depositStatus.finalized_block}`}
                  </p>
                )}
                <p className="text-xs text-slate-400">USDT only. Native TRX, ETH, BNB and other tokens do not earn points.</p>
                {!manualDeposits && depositNotifications[network]?.status && depositNotifications[network].status !== "disabled" && (
                  <p className="text-xs text-slate-400">
                    {depositNotifications[network].status === "active"
                      ? "Deposit notifications are registered. Credits still require network confirmation."
                      : "Deposit notification setup is pending. Please wait before sending new funds."}
                  </p>
                )}
                <label className="block text-xs text-slate-400" htmlFor="deposit-network">Network</label>
                <select
                  id="deposit-network"
                  data-testid="deposit-network"
                  value={network}
                  onChange={(event) => { setNetwork(event.target.value); setCopied(false); }}
                  className="w-full rounded-lg bg-[#0B0E14] border border-[#1E293B] p-3 text-sm"
                >
                  {Object.entries(networkNames).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                {depositAddress && (
                  <div className="rounded-xl border border-[#1E293B] bg-[#0B0E14] p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Your {networkNames[network]} address</p>
                    <div className="flex items-center gap-2">
                      <code data-testid="deposit-address-text" className="font-mono-num text-xs text-[#00E676] break-all">
                        {depositAddress}
                      </code>
                      <button aria-label="Copy deposit address" data-testid="copy-deposit-address-btn" onClick={copy} className="shrink-0">
                        {copied ? <Check size={16} className="text-[#00E676]" /> : <Copy size={16} className="text-slate-400" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-3">This address is for {networkNames[network]} only.</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog>
            <DialogTrigger asChild>
              <button
                data-testid="open-withdraw-dialog"
                className="rounded-xl border border-[#1E293B] bg-[#121721] p-4 flex flex-col items-center gap-2"
              >
                <ArrowUpFromLine className="text-[#FF3B30]" size={22} />
                <span className="text-sm font-semibold">Withdraw</span>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#121721] border-[#1E293B] text-white max-w-sm">
              <DialogHeader>
                <DialogTitle className="font-display">Withdraw USDT</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-xl border border-[#1E293B] bg-[#0B0E14] p-3 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Withdrawals unlocked</span>
                    <span className={user.withdraw_unlocked ? "text-[#00E676]" : "text-[#F59E0B]"}>
                      {user.withdraw_unlocked ? "Yes" : "Deposit 50 USDT"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Max per day</span>
                    <span className="font-mono-num text-slate-200">{maxWithdraw} USDT</span>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 mb-1">Receiving Address</p>
                  <div className="flex gap-2">
                    <Input
                      data-testid="receiving-address-input"
                      value={recvAddr}
                      onChange={(e) => setRecvAddr(e.target.value)}
                      placeholder="Your external wallet address"
                      className="bg-[#0B0E14] border-[#1E293B] text-white font-mono-num text-xs"
                    />
                    <Button
                      data-testid="save-receiving-address-btn"
                      onClick={saveAddr}
                      disabled={busy === "addr"}
                      className="bg-[#1E2330] hover:bg-[#2A3242] text-white text-xs px-3"
                    >
                      Save
                    </Button>
                  </div>
                </div>
                <Input
                  data-testid="withdraw-amount-input"
                  type="number"
                  value={wdAmount}
                  onChange={(e) => setWdAmount(e.target.value)}
                  placeholder="Amount (USDT)"
                  className="bg-[#0B0E14] border-[#1E293B] text-white font-mono-num"
                />
                <Button
                  data-testid="withdraw-submit-btn"
                  onClick={doWithdraw}
                  disabled={busy === "withdraw"}
                  className="w-full h-11 bg-[#FF3B30] hover:bg-[#D50000] text-white font-bold rounded-xl"
                >
                  {busy === "withdraw" ? <Loader2 className="animate-spin" size={18} /> : "Request Withdrawal"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* progress card */}
        <div className="rounded-2xl border border-[#1E293B] bg-[#121721] p-5 space-y-4">
          <h3 className="font-semibold text-sm">Your Progress</h3>
          <StepRow done={user.total_deposited >= 50} label="Deposit 50 USDT to unlock withdrawals" />
          <StepRow done={user.total_deposited >= 100} label="Deposit 100 USDT to earn a 10 USDT bonus (one-time)" />
          <div className="text-xs text-slate-500 leading-relaxed pt-1 border-t border-[#1E293B]">
            Withdrawal tiers: balance &lt; 200 → 20/day · ≥ 200 → 45/day · ≥ 500 → 70/day · ≥ 1000 →
            100/day. Max 1 withdrawal per day.
          </div>
        </div>

        {/* transactions */}
        <div className="rounded-2xl border border-[#1E293B] bg-[#121721] overflow-hidden">
          <h3 className="font-semibold text-sm px-4 pt-4 pb-2">Recent Activity</h3>
          {txns.length === 0 ? (
            <p className="text-xs text-slate-500 px-4 pb-4">No transactions yet.</p>
          ) : (
            txns.map((t, i) => {
              const pos = t.amount > 0;
              const labels = {
                deposit: "Deposit",
                withdraw: "Withdrawal",
                trade_win: "Trade Win",
                trade_loss: "Trade Loss",
                referral_bonus: "Referral Bonus",
                referral_reward: "Referral Reward",
                deposit_bonus: "Deposit Bonus",
                commission: "Chain Commission",
              };
              return (
                <div
                  key={i}
                  data-testid={`txn-row-${i}`}
                  className="flex items-center justify-between px-4 py-3 border-t border-[#1E293B]/60"
                >
                  <div>
                    <div className="text-sm">{labels[t.type] || t.type}</div>
                    <div className="text-[10px] text-slate-500 font-mono-num">
                      {t.created_at ? new Date(t.created_at).toLocaleString() : ""}
                    </div>
                    {t.type === "deposit" && t.tx_hash && (
                      <a className="text-xs text-[#00E676]" target="_blank" rel="noopener noreferrer"
                        href={t.network === "TRX" ? `https://tronscan.org/#/transaction/${t.tx_hash}`
                          : `https://${t.network === "BSC" ? "bscscan.com" : "etherscan.io"}/tx/0x${t.tx_hash}`}>
                        {t.network} · Confirmed deposit
                      </a>
                    )}
                  </div>
                  <span
                    className={`font-mono-num text-sm font-semibold ${
                      t.type === "withdraw" ? "text-slate-300" : pos ? "text-[#00E676]" : "text-[#FF3B30]"
                    }`}
                  >
                    {t.type === "withdraw" ? "-" : pos ? "+" : ""}
                    {Math.abs(t.amount).toFixed(2)}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
